import {
  Editor,
  Geometry2d,
  rng,
  ShapeUtil,
  SVGContainer,
  SvgExportContext,
  TLDrawShape,
  TLDrawShapeSegment,
  TLResizeInfo,
  toFixed,
  Vec,
} from "tldraw"
import {
  MarkerShape,
  MarkerShapeProps,
  markerShapeProps,
} from "../../shared/MarkerShape"
import { DrawShapeUtil } from "tldraw"
import { Color, Expr } from "../../shared/Expr"
import { LDPage } from "../../shared/LDPage"
import { LiveDraw } from "../LiveDraw"
import { last } from "lodash"
import { getFreehandOptions } from "./draw/getPath"
import { getSvgPathFromStrokePoints } from "./shared/freehand/svg"
import { getStrokePoints } from "./shared/freehand/getStrokePoints"
import { svgInk, svgInk2 } from "./shared/freehand/svgInk"
import { getFillDefForExport } from "./defaultStyleDefs"
import { JSX } from "react"
import React from "react"
import { StrokeOptions } from "./shared/freehand/types"

export class MarkerShapeUtil extends ShapeUtil<MarkerShape> {
  static override type = "marker-shape" as const
  static override props = markerShapeProps
  // static override migrations = drawShapeMigrations

  underlying: DrawShapeUtil

  constructor(editor: Editor) {
    super(editor)

    // delegate to DrawShapeUtil for geometry stuff
    this.underlying = new DrawShapeUtil(editor)
  }

  getDefaultProps(): MarkerShapeProps {
    return {
      color: Expr.lit("brown" as Color),
      radius: 10,
      segments: [],
      isComplete: false,
      isPen: false,
      scale: 1,
      layer: "stroke",
    }
  }

  getGeometry(shape: MarkerShape): Geometry2d {
    const proxyShape: TLDrawShape = toProxyShape(shape)
    return this.underlying.getGeometry(proxyShape)
  }
  component(shape: MarkerShape) {
    const pageId = this.editor.getAncestorPageId(shape)!
    const pageMeta = (this.editor.getPage(pageId)! as LDPage).meta
    const layer = pageMeta.layers[shape.props.layer]
    const isHovered = this.editor.getHoveredShapeId() === shape.id
    const isEraser = this.editor.getCurrentTool()?.id === "eraser2"

    if (!layer.visible) return null

    return (
      <SVGContainer>
        <MarkerShapeSvg
          hovered={isHovered}
          shape={shape}
          color={
            isEraser
              ? "grey"
              : Expr.evaluate(shape.props.color, pageMeta.parameterValues)
          }
          strokeWidth={shape.props.radius * 2}
          opacity={
            isEraser
              ? 0.1
              : Expr.evaluate(layer.opacity, pageMeta.parameterValues)
          }
        />
      </SVGContainer>
    )
  }
  indicator(shape: MarkerShape): JSX.Element {
    return this.underlying.indicator(toProxyShape(shape))
  }

  override toSvg(shape: MarkerShape, ctx: SvgExportContext) {
    const pageId = this.editor.getAncestorPageId(shape)!

    ctx.addExportDef(getFillDefForExport(/*shape.props.fill*/ "fill"))
    const scaleFactor = 1 / shape.props.scale
    const pageMeta = (this.editor.getPage(pageId)! as LDPage).meta
    const layer = pageMeta.layers[shape.props.layer]
    return (
      <g transform={`scale(${scaleFactor})`}>
        <MarkerShapeSvg
          hovered={false}
          shape={shape}
          color={Expr.evaluate(shape.props.color, pageMeta.parameterValues)}
          strokeWidth={shape.props.radius * 2}
          opacity={Expr.evaluate(layer.opacity, pageMeta.parameterValues)}
        />
      </g>
    )
  }

  override onResize(shape: MarkerShape, info: TLResizeInfo<MarkerShape>) {
    const { scaleX, scaleY } = info

    const newSegments: TLDrawShapeSegment[] = []

    for (const segment of shape.props.segments) {
      newSegments.push({
        ...segment,
        points: segment.points.map(({ x, y, z }) => {
          return {
            x: toFixed(scaleX * x),
            y: toFixed(scaleY * y),
            z,
          }
        }),
      })
    }

    return {
      props: {
        segments: newSegments,
      },
    }
  }
}

export function toProxyShape(shape: MarkerShape): TLDrawShape {
  return {
    ...shape,
    type: "draw",
    props: {
      ...shape.props,
      isClosed: false,
      color: "black",
      fill: "none",
      dash: "draw",
      size: "m",
    },
  }
}

function MarkerShapeSvg({
  hovered,
  shape,
  color,
  strokeWidth,
  opacity,
}: {
  hovered: boolean
  shape: MarkerShape
  color: string
  strokeWidth: number

  opacity: number
}) {
  const proxyShape = toProxyShape(shape)

  console.log("segments", shape.props.segments)

  const allPointsFromSegments = getPointsFromSegments(shape.props.segments)

  const showAsComplete =
    shape.props.isComplete || last(shape.props.segments)?.type === "straight"

  let sw = strokeWidth * shape.props.scale

  if (
    !shape.props.isPen &&
    // shape.props.dash === 'draw' &&
    allPointsFromSegments.length === 1
  ) {
    sw += rng(shape.id)() * (sw / 6)
  }

  const options: StrokeOptions = {
    thinning: 0,
    streamline: 0,
    smoothing: 0,
    size: sw * 2.65,
    simulatePressure: false,
  }
  console.log("svgPath", svgInk2(allPointsFromSegments, options))

  return (
    <path
      d={svgInk2(allPointsFromSegments, options)}
      stroke={color}
      strokeWidth={strokeWidth}
      opacity={opacity}
      fill="none"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  )
}

export function getPointsFromSegments(segments: TLDrawShapeSegment[]) {
  const points: Vec[] = []

  for (const segment of segments) {
    if (segment.type === "free" || segment.points.length < 2) {
      points.push(...segment.points.map(Vec.Cast))
    } else {
      const pointsToInterpolate = Math.max(
        4,
        Math.floor(Vec.Dist(segment.points[0], segment.points[1]) / 16),
      )
      points.push(
        ...Vec.PointsBetween(
          segment.points[0],
          segment.points[1],
          pointsToInterpolate,
        ),
      )
    }
  }

  return points
}
